import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { IntelligenceFormatService }    from '../intelligence.format.service';
import { IntelligenceNarrativeService } from '../intelligence.narrative.service';
import { IntelligenceService }          from '../intelligence.service';
import { IntelligenceAskTurnModel }     from '../typings/intelligence.ask-turn';
import { IntelligenceSummaryModel }     from '../typings/intelligence.summary';

const HISTORY_TURNS = 8;

@Component({
  selector   : 'wor-intelligence-overview-tab',
  template: require('./intelligence.overview-tab.component.html')
})
export class OverviewTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public askAsked            : string  = '';
  public askError            : string  = '';
  public askLimitReached     : boolean = false;
  public askLoading          : boolean = false;
  public askQuestion         : string  = '';
  public askUpgradeAvailable : boolean = false;

  public askTurns : Array<IntelligenceAskTurnModel>  = [];
  public summary  : IntelligenceSummaryModel         = null;

  public askSuggestions : Array<string> = [
    this.translateService.instant('INTELLIGENCE_SPACE.ASK.SUGGESTION_1'),
    this.translateService.instant('INTELLIGENCE_SPACE.ASK.SUGGESTION_2'),
    this.translateService.instant('INTELLIGENCE_SPACE.ASK.SUGGESTION_3'),
    this.translateService.instant('INTELLIGENCE_SPACE.ASK.SUGGESTION_4')
  ];

  @ViewChild('askThread') private askThread : ElementRef<HTMLDivElement>;

  private askGeneration  : number = 0;
  private loadGeneration : number = 0;
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService   : IntelligenceFormatService,
    private intelligenceNarrativeService: IntelligenceNarrativeService,
    public intelligenceService          : IntelligenceService,
    private translateService            : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    // The ask card is not tied to the date picker - the AI chooses its own period from
    // the question - so an answer stays put when the range changes.
    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  ask ( question = this.askQuestion ) : void {
    const trimmed = ( question || '' ).trim();

    if ( !trimmed || this.askLoading ) {
      return;
    }

    const generation = ++this.askGeneration;

    this.askAsked = trimmed;
    this.askError = '';
    this.askLimitReached = false;
    this.askLoading = true;
    this.askQuestion = '';
    this.askUpgradeAvailable = false;

    this.followAskThread();

    this.intelligenceNarrativeService.ask(trimmed, this.askTurns.slice(-HISTORY_TURNS))
    .then(answer => {
      if ( generation !== this.askGeneration ) {
        return;
      }

      // Out of allowance is not a failure: the card offers the upgrade rather than
      // telling the owner something went wrong.
      if ( answer.limitReached ) {
        this.askLimitReached = true;
        this.askUpgradeAvailable = answer.upgradeAvailable;

        return;
      }

      if ( !answer.text ) {
        this.askError = this.translateService.instant('INTELLIGENCE_SPACE.ASK.ERROR');

        return;
      }

      this.askTurns = [ ...this.askTurns, { answer: answer.text, question: trimmed } ];
      this.askAsked = '';
    })
    .catch(() => {
      if ( generation === this.askGeneration ) {
        this.askError = this.translateService.instant('INTELLIGENCE_SPACE.ASK.ERROR');
      }
    })
    .finally(() => {
      if ( generation === this.askGeneration ) {
        this.askLoading = false;

        this.followAskThread();
      }
    });
  }

  clearAsk () : void {
    this.askGeneration += 1;
    this.askAsked = '';
    this.askError = '';
    this.askLimitReached = false;
    this.askLoading = false;
    this.askQuestion = '';
    this.askTurns = [];
    this.askUpgradeAvailable = false;
  }

  count ( key : string ) : string {
    return this.summary && this.summary.kpis[ key ]
      ? this.intelligenceFormatService.count(this.summary.kpis[ key ].value)
      : '';
  }

  delta ( key : string ) : number {
    return this.summary && this.summary.kpis[ key ]
      ? this.summary.kpis[ key ].delta_pct
      : null;
  }

  grossMargin () : string {
    if ( !this.summary ) {
      return '';
    }

    const grossProfit = this.summary.kpis[ 'gross_profit' ];
    const revenue = this.summary.kpis[ 'revenue' ];

    return !grossProfit || !revenue || !revenue.value
      ? this.intelligenceFormatService.percent(null)
      : this.intelligenceFormatService.percent(grossProfit.value / revenue.value * 100);
  }

  grossMarginDelta () : number {
    if ( !this.summary ) {
      return null;
    }

    const grossProfit = this.summary.kpis[ 'gross_profit' ];
    const revenue = this.summary.kpis[ 'revenue' ];

    if ( !grossProfit || !revenue || !revenue.value || !revenue.prior ) {
      return null;
    }

    return ( grossProfit.value / revenue.value * 100 ) - ( grossProfit.prior / revenue.prior * 100 );
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getSummary()
    .then(( data : IntelligenceSummaryModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      if ( !data || !data.kpis ) {
        this.error = true;
        this.summary = null;

        return;
      }

      this.summary = data;
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  money ( key : string ) : string {
    return this.summary && this.summary.kpis[ key ]
      ? this.intelligenceFormatService.money(this.summary.kpis[ key ].value)
      : '';
  }

  receivables () : string {
    return this.summary && this.summary.receivables
      ? this.intelligenceFormatService.money(this.summary.receivables.outstanding)
      : '';
  }

  wipCaption () : string {
    if ( !this.summary || !this.summary.work_in_progress ) {
      return '';
    }

    const wip = this.summary.work_in_progress;
    const jobs = this.translateService.instant('INTELLIGENCE_SPACE.COMMON.JOBS');
    const grossProfit = this.translateService.instant('INTELLIGENCE_SPACE.COMMON.GROSS_PROFIT');

    return `${ wip.job_count } ${ jobs } · ${ this.intelligenceFormatService.money(wip.estimated_gross_profit) } ${ grossProfit }`;
  }

  wipValue () : string {
    return this.summary && this.summary.work_in_progress
      ? this.intelligenceFormatService.money(this.summary.work_in_progress.value_ex_tax)
      : '';
  }

  // Waits a tick so the new message is in the DOM, then keeps the thread pinned to the
  // latest exchange the way any chat window follows its newest message.
  private followAskThread () : void {
    setTimeout(() => {
      if ( this.askThread ) {
        this.askThread.nativeElement.scrollTop = this.askThread.nativeElement.scrollHeight;
      }
    });
  }
}
